import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  createDrill,
  deleteDrill,
  getDrill,
  getDrillDetails,
  getDrills,
  updateDrill,
} from '../api/drillAPI';
import {Drill, DrillDetailRecord} from '../interfaces';

type RequestStatus = 'idle' | 'loading' | 'failed';

export interface DrillState {
  drills: Drill[] | null;
  drill: Drill | null;
  drillDetails: DrillDetailRecord[] | null;
  status: RequestStatus;
  error: unknown;
}

const initialState: DrillState = {
  drills: null,
  drill: null,
  drillDetails: null,
  status: 'idle',
  error: null,
};

type RejectPayload = {name: string; message: string};

const getDrillsAsync = createAsyncThunk<
  Drill[],
  {teamId: number},
  {rejectValue: RejectPayload}
>('drill/getDrills', async ({teamId}, {rejectWithValue}) => {
  try {
    const response = await getDrills({teamId});
    return response;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({name: error.name, message: error.message});
  }
});

const getDrillAsync = createAsyncThunk<
  Drill,
  {id: number},
  {rejectValue: RejectPayload}
>('drill/getDrill', async ({id}, {rejectWithValue}) => {
  try {
    const response = await getDrill({id});
    return response;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({name: error.name, message: error.message});
  }
});

const getDrillDetailsAsync = createAsyncThunk<
  DrillDetailRecord[],
  {drillId: number},
  {rejectValue: RejectPayload}
>('drill/getDrillDetails', async ({drillId}, {rejectWithValue}) => {
  try {
    const response = await getDrillDetails({drillId});
    return response;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({name: error.name, message: error.message});
  }
});

const createDrillAsync = createAsyncThunk<
  Drill,
  {name: string; description?: string; teamId: number},
  {rejectValue: RejectPayload}
>('drill/createDrill', async (input, {rejectWithValue}) => {
  try {
    const drill = await createDrill(input);
    return drill;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({name: error.name, message: error.message});
  }
});

const updateDrillAsync = createAsyncThunk<
  Drill,
  {id: number; name?: string; description?: string},
  {rejectValue: RejectPayload}
>('drill/updateDrill', async (input, {rejectWithValue}) => {
  try {
    const response = await updateDrill(input);
    return response;
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({name: error.name, message: error.message});
  }
});

const deleteDrillAsync = createAsyncThunk<
  {id: number},
  {id: number},
  {rejectValue: RejectPayload}
>('drill/deleteDrill', async ({id}, {rejectWithValue}) => {
  try {
    const response = await deleteDrill({id});
    const success: boolean = Boolean(response?.success);
    if (!success) {
      throw new Error('Failed to delete drill');
    }
    return {id};
  } catch (err) {
    const error = err as Error;
    return rejectWithValue({name: error.name, message: error.message});
  }
});

export const drillSlice = createSlice({
  name: 'drill',
  initialState,
  reducers: {
    setActiveDrill(state, action: PayloadAction<Drill | null>) {
      state.drill = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDrillsAsync.pending, state => {
        state.status = 'loading';
      })
      .addCase(getDrillsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.drills = action.payload;
        state.error = null;
      })
      .addCase(getDrillsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.drills = [];
        state.error = action.payload;
      })
      .addCase(getDrillAsync.pending, state => {
        state.drill = null;
        state.status = 'loading';
      })
      .addCase(getDrillAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.drill = action.payload;
        state.error = null;
      })
      .addCase(getDrillAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.drill = null;
        state.error = action.payload;
      })
      .addCase(getDrillDetailsAsync.pending, state => {
        state.drillDetails = null;
      })
      .addCase(getDrillDetailsAsync.fulfilled, (state, action) => {
        state.drillDetails = action.payload;
        state.error = null;
      })
      .addCase(getDrillDetailsAsync.rejected, (state, action) => {
        state.drillDetails = [];
        state.error = action.payload;
      })
      .addCase(createDrillAsync.pending, state => {
        state.status = 'loading';
      })
      .addCase(createDrillAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.drill = action.payload;
        state.error = null;
      })
      .addCase(createDrillAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(updateDrillAsync.pending, state => {
        state.status = 'loading';
      })
      .addCase(updateDrillAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.drill = action.payload;
        state.error = null;
      })
      .addCase(updateDrillAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(deleteDrillAsync.pending, state => {
        state.status = 'loading';
      })
      .addCase(deleteDrillAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        if (state.drill && state.drill.id === action.payload.id) {
          state.drill = null;
        }
        state.error = null;
      })
      .addCase(deleteDrillAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const {setActiveDrill} = drillSlice.actions;

export default drillSlice.reducer;

export {
  getDrillsAsync,
  getDrillAsync,
  getDrillDetailsAsync,
  createDrillAsync,
  updateDrillAsync,
  deleteDrillAsync,
};
